import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightAndDownLeftFromCenter } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@progress/kendo-react-buttons';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { Window } from "@progress/kendo-react-dialogs";
import { useQuery } from 'react-query';
import SelectedAssetMetricsReadDto from '../../../../../../Models/Dashboard/SelectedAssetMetricsReadDto';
import LoadingSpinner from '../../../../../Shared/Components/LoadingSpinner';
// import ErrorCard from '../../../../Shared/Components/Cards/ErrorCard';
import assetsDashboardApiService from '../../../Utilities/AssetsDashboardApiService';
import moment from 'moment';
import Chart from '../../../../../SiteExplorer/Components/Charts/Base/Chart';
import { ChartToolbar } from '../../../../../SiteExplorer/Utilities/ChartToolbar';
import useChartStore from '../../../../../../Zustand/chartStore';
import { FuelBurnTableComponent } from '../../../../../SiteExplorer/Components/Charts/FuelBurn/FuelBurnTableComponent';
import NoDataPage from '../../../../../SiteExplorer/Pages/NoDataPage';

interface Props {
  assetData: SelectedAssetMetricsReadDto;
  dateFilter: { startDate: Date, endDate: Date };
  setDateFilter: (filter: { startDate: Date, endDate: Date }) => void;
}

export interface FuelBurnChartComponentRef {
  refresh: () => Promise<void>;
  getWindowHeight: () => number | null;
  windowHeight: number | null;
}

const FuelBurnChartComponent = forwardRef<FuelBurnChartComponentRef, Props>(({ assetData, dateFilter, setDateFilter }, ref) => {
  const [open, setOpen] = useState(false);
  const [windowHeight, setWindowHeight] = useState<number | null>(null);
  const windowRef = useRef<any>(null);
  const { viewType, setViewType } = useChartStore((state) => ({ 
    viewType: state.viewType, 
    setViewType: state.setViewType 
  }));

  // Set viewType to 'line' when component mounts
  useEffect(() => {
    if (viewType !== 'line' && viewType !== 'userLine' && viewType !== 'table') {
      setViewType('line');
    }
  }, [viewType, setViewType]);

  const handleStartDateChange = (event: any) => {
    if (event.value) {
      setDateFilter({
        ...dateFilter,
        startDate: moment(event.value).startOf('day').toDate(),
        endDate: dateFilter.endDate,
      });
    }
  };

  const handleEndDateChange = (event: any) => {
    if (event.value) {
      setDateFilter({
        ...dateFilter,
        endDate: moment(event.value).endOf('day').toDate(),
        startDate: dateFilter.startDate,
      });
    }
  };

  // Function to get the real height of the window
  const getWindowHeight = () => {
    if (windowRef.current) {
      const windowElement = windowRef.current.element;
      if (windowElement) {
        return windowElement.offsetHeight;
      }
    }
    return null;
  };

  // Use ResizeObserver to track window size changes
  useEffect(() => {
    if (open && windowRef.current) {
      const windowElement = windowRef.current.element;
      if (windowElement) {
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            setWindowHeight(entry.contentRect.height);
          }
        });
        
        resizeObserver.observe(windowElement);
        
        // Set initial height
        setWindowHeight(windowElement.offsetHeight);
        
        return () => {
          resizeObserver.disconnect();
        };
      }
    } else {
      setWindowHeight(null);
    }
    
    return undefined;
  }, [open]);

  // Fuel Burn API call - get chart data
  const fuelBurnChartDataQuery = useQuery(
    ['locoFuelBurnChartData', assetData.id, dateFilter.startDate, dateFilter.endDate],
    async () => {
      return await assetsDashboardApiService.getLocoFuelBurnChartData(
        assetData.id,
        dateFilter.startDate,
        dateFilter.endDate
      );
    },
    {
      enabled: !!assetData?.id && !!dateFilter.startDate && !!dateFilter.endDate,
      keepPreviousData: true
    }
  );

  // Fuel Burn API call - get table data (only fetch when in table view and fullscreen)
  const fuelBurnTableDataQuery = useQuery(
    ['locoFuelBurn', assetData.id, dateFilter.startDate, dateFilter.endDate],
    async () => {
      return await assetsDashboardApiService.getLocoFuelBurn(
        assetData.id,
        dateFilter.startDate,
        dateFilter.endDate
      );
    },
    {
      enabled: !!assetData?.id && !!dateFilter.startDate && !!dateFilter.endDate && viewType === 'table' && open,
      keepPreviousData: true
    }
  );

  // Use API data directly - just map field names to what chart expects
  const apiData = fuelBurnChartDataQuery.data;
  const isLoading = fuelBurnChartDataQuery.isLoading || fuelBurnChartDataQuery.isFetching;
  
  // Check if we have data to display - only check after initial load is complete
  const hasChartData = !isLoading && apiData && (
    (Array.isArray(apiData.timestamps) && apiData.timestamps.length > 0) ||
    (Array.isArray(apiData.fuelBurnRatesTempComp) && apiData.fuelBurnRatesTempComp.length > 0) ||
    (Array.isArray(apiData.speeds) && apiData.speeds.length > 0)
  );
  
  const hasTableData = viewType === 'table' && open && !fuelBurnTableDataQuery.isLoading && !fuelBurnTableDataQuery.isFetching && fuelBurnTableDataQuery.data && 
    Array.isArray(fuelBurnTableDataQuery.data) && fuelBurnTableDataQuery.data.length > 0;

  const chartData = useMemo(() => {
    const timestamps = apiData?.timestamps || [];
    // Always use dateFilter for visibleRange, not the data timestamps
    // This ensures the chart shows the full date range selected, not just the data range
    const visibleRange = {
      min: moment(dateFilter.startDate).unix(),
      max: moment(dateFilter.endDate).unix()
    };

    return {
      dateFilter: dateFilter,
      timestampFuelBurn: timestamps,
      dataFuelBurn: apiData?.fuelBurnRatesTempComp || [],
      timestampSpeed: timestamps,
      dataSpeed: apiData?.speeds || [],
      visibleRange: visibleRange,
      aggregator: 'none'
    };
  }, [apiData, dateFilter]);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      await fuelBurnChartDataQuery.refetch();
    },
    getWindowHeight: getWindowHeight,
    windowHeight: windowHeight
  }));

  const handleWindowClose = () => {
    setOpen(false);
  };

  const renderChartContent = (isFullscreen: boolean) => {
    // Create a key based on dates and data availability to force chart re-render
    const hasData = apiData && (
      (Array.isArray(apiData.timestamps) && apiData.timestamps.length > 0) ||
      (Array.isArray(apiData.fuelBurnRatesTempComp) && apiData.fuelBurnRatesTempComp.length > 0) ||
      (Array.isArray(apiData.speeds) && apiData.speeds.length > 0)
    );
    const chartKey = `fuel-burn-${dateFilter.startDate.getTime()}-${dateFilter.endDate.getTime()}-${hasData ? 'has-data' : 'no-data'}`;
    
    return (
      <div className="flex-1" style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Chart
          key={chartKey}
          chartType="fuel-burn"
          chartData={chartData}
          isSimpleChart={!isFullscreen}
          chartParentHeight={isFullscreen ? (windowHeight ? windowHeight - 200 : 400) : 350}
          isFullscreen={isFullscreen}
        />
        <div className="flex flex-wrap">
          {isFullscreen && <div id="legend-fuel-burn-fullscreen"></div>}
          {!isFullscreen && <div id="legend-fuel-burn"></div>}
        </div>
      </div>
    );
  };

  const renderLayout = (isFullscreen: boolean) => (
    <div
      style={
        isFullscreen
          ? { height: "80vh", display: "flex", flexDirection: "column" }
          : { height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }
      }
    >
      <div className={`flex ${isFullscreen ? 'justify-between mb-4' : 'flex-wrap justify-between items-center gap-y-2 mb-2'}`}>
        <div className="text-lg font-semibold">Fuel Burn</div>
        <div className='flex items-center gap-x-2'>
          <DatePicker
            value={dateFilter.startDate}
            onChange={handleStartDateChange}
            max={dateFilter.endDate || undefined}
            width="150px"
          />
          <span>-</span>
          <DatePicker
            value={dateFilter.endDate}
            onChange={handleEndDateChange}
            min={dateFilter.startDate || undefined}
            max={moment().toDate()}
            width="150px"
          />
          {!isFullscreen && (
            <Button onClick={() => setOpen(true)}>
              <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
            </Button>
          )}
        </div>
      </div>
      {isFullscreen && (
        <ChartToolbar
          title={null}
          lastSeen={null}
          showAutoRange={true}
          showAggregatorOptions={false}
          showChartOptions={true}
          showThresholds={false}
          showSingleMultipleChart={false}
          showTable={true}
          showBothCharts={false}
        />
      )}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', marginTop: isFullscreen ? '8px' : '0' }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full w-full">
            <LoadingSpinner />
          </div>
        ) : viewType === 'table' && isFullscreen ? (
          hasTableData ? (
            <FuelBurnTableComponent
              data={fuelBurnTableDataQuery.data}
              isFullscreen={isFullscreen}
              isLoading={fuelBurnTableDataQuery.isLoading || fuelBurnTableDataQuery.isFetching}
            />
          ) : (
            <NoDataPage />
          )
        ) : (
          hasChartData ? (
            renderChartContent(isFullscreen)
          ) : (
            <NoDataPage />
          )
        )}
      </div>
    </div>
  );

  // if (fuelBurnQuery.isLoading) {
  //   return <LoadingSpinner />;
  // } else if (fuelBurnQuery.isError) {
  //   return <ErrorCard />;
  // }

  return (
    <div className="h-full flex flex-col p-2">
      {!open && renderLayout(false)}
      {open && (
        <Window
          ref={windowRef}
          title={`${assetData?.name ? `${assetData.name} - ` : ''}Fuel Burn`}
          onClose={handleWindowClose}
          style={{ top: "5vh", left: "5vw", width: "90vw", height: "90vh" }}
          modal={true}
          className="custom-fullscreen-window"
        >
          {renderLayout(true)}
        </Window>
      )}
    </div>
  );
});

FuelBurnChartComponent.displayName = 'FuelBurnChartComponent';

export default FuelBurnChartComponent;

